import React, { useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import { IconButton } from "./EventList";
import { GAME_STATUS_OPTIONS, GameState, gameStatusFor } from "./gameStatus";
import "./Scoreboard.css";

interface GameStatusControlProps {
  state: GameState;
  isSaving: boolean;
  onChange: (state: GameState) => void;
}

/**
 * The game's status, and — for a scorekeeper — the way to change it.
 *
 * The badge is what the page shows at rest; the pencil swaps it for the three
 * statuses as buttons, so ending a game is one tap rather than a trip back to
 * the schedule. Marking a game final asks first: it publishes the score to the
 * public schedule and counts the result towards the standings, which is not
 * something to do by brushing the screen. Going back to Live does not ask,
 * since that is how a premature tap gets undone.
 *
 * There is no read-only mode: GameScoringPage turns the whole scoreboard away
 * from anyone who cannot score the game, so this is only ever rendered for
 * someone allowed to change the status.
 */
export default function GameStatusControl({
  state,
  isSaving,
  onChange,
}: GameStatusControlProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingFinal, setIsConfirmingFinal] = useState(false);

  const current = gameStatusFor(state);

  const handleSelect = (next: GameState) => {
    if (next === state) {
      setIsEditing(false);
      return;
    }
    if (next === "completed") {
      setIsConfirmingFinal(true);
      return;
    }
    setIsEditing(false);
    onChange(next);
  };

  const confirmFinal = () => {
    setIsConfirmingFinal(false);
    setIsEditing(false);
    onChange("completed");
  };

  return (
    <>
      {isEditing ? (
        <div className="sb-status-edit" role="group" aria-label="Game status">
          {GAME_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`sb-status-btn${
                option.value === state ? " sb-status-btn--selected" : ""
              }`}
              aria-pressed={option.value === state}
              disabled={isSaving}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
          <Button
            size="sm"
            variant="link"
            className="sb-status-cancel"
            disabled={isSaving}
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <span className="sb-gameinfo-status">
          <Badge bg={current.variant} className="sb-gameinfo-badge">
            {current.label}
          </Badge>
          <IconButton
            icon="pencil"
            label="Change game status"
            onClick={() => setIsEditing(true)}
          />
        </span>
      )}

      <Modal
        show={isConfirmingFinal}
        onHide={() => setIsConfirmingFinal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Finish this game?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The score goes onto the public schedule and counts towards the
          standings. You can set the game back to Live if you need to keep
          scoring.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsConfirmingFinal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmFinal}>
            Finish game
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
